"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "../../utils";
import { usePortalContainer } from "../portal-container/PortalContainer";

const TooltipProvider = TooltipPrimitive.Provider;

const TooltipCore = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal container={usePortalContainer()}>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        // The max-width is what makes a long tooltip wrap. Without one the
        // content sizes to its own single line (Radix's popper wrapper is
        // `min-width: max-content`), so a sentence-length tooltip renders as
        // one ~1000px line that runs off screen instead of wrapping. Capping
        // the content is enough on its own — the wrapper shrinks to match.
        // 500px is an arbitrary value on purpose: the default scale jumps
        // 448px (`md`) to 512px (`lg`) with nothing at 500.
        // `al-break-words` covers the other overflow: one long unbroken token
        // (a URL, an identifier) that cannot wrap at a space.
        "al-z-50 al-max-w-[500px] al-overflow-hidden al-break-words al-rounded-md al-border al-bg-popover al-px-3 al-py-1.5 al-text-sm al-text-popover-foreground al-shadow-md al-animate-in al-fade-in-0 al-zoom-in-95 data-[state=closed]:al-animate-out data-[state=closed]:al-fade-out-0 data-[state=closed]:al-zoom-out-95 data-[side=bottom]:al-slide-in-from-top-2 data-[side=left]:al-slide-in-from-right-2 data-[side=right]:al-slide-in-from-left-2 data-[side=top]:al-slide-in-from-bottom-2",
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// Type that enforces single non-fragment child for proper positioning
type SingleNonFragmentChild = React.ReactElement<
  unknown,
  Exclude<React.ElementType, typeof React.Fragment>
>;

const Tooltip = ({
  children,
  title,
  contentProps,
  ...props
}: TooltipPrimitive.TooltipProps & {
  title?: string | React.ReactNode;
  children: SingleNonFragmentChild;
  // Forwarded to TooltipContent — e.g. `style`/`className` to lift the
  // tooltip above an overlapping portal layer such as an open Select panel.
  contentProps?: React.ComponentPropsWithoutRef<typeof TooltipContent>;
}) => {
  if (!title) {
    return <>{children}</>;
  }

  return (
    <TooltipCore {...props}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent {...contentProps}>{title}</TooltipContent>
    </TooltipCore>
  );
};

export { Tooltip, TooltipCore, TooltipTrigger, TooltipContent, TooltipProvider };
